/**
 * /me — โชว์ตัวละคร 3 แบบ · Download กลับบ้าน · สถานะการ gen · ปุ่มลบข้อมูล (PDPA)
 *
 * หน้าตาอิงจาก `mockups/player_you_register.html` (การ์ด 3 แบบ `.sty` ที่เลือกแล้ว
 * ขอบเป็นเขียวมะนาว · ปุ่ม Download สีสว่างเป็นปุ่มหลัก · แถบท้ายบอกสถานะ)
 */
import { AVATAR, FRAMES_PER_SHEET, type StyleId } from "@py/protocol";

import { ENV } from "../env.js";
import { clearSession, loadSession, saveSession, type Session } from "../session.js";

const LABEL: Record<StyleId, { name: string; base: string }> = {
  1: { name: "8-BIT", base: "ฐาน 1–2" },
  2: { name: "16-BIT", base: "ฐาน 3" },
  3: { name: "HI-BIT", base: "ฐาน 4" },
  4: { name: "ANIME", base: "ฐาน 5" },
};

export function renderMe(root: HTMLElement): void {
  const s = loadSession();
  if (!s) {
    root.innerHTML = `
      <div class="title">PLAYER YOU</div>
      <div class="kick">NO CHARACTER YET</div>
      <div class="lbl">ยังไม่มีตัวละคร</div>
      <div class="note">สแกน QR ที่จอลงทะเบียนเพื่อเริ่มครับ</div>`;
    return;
  }

  root.innerHTML = `
    <div class="title">PLAYER YOU</div>
    <div class="kick">YOUR FOUR FORMS</div>

    <div class="lbl">สถานะ</div>
    <!-- แยกบรรทัดสถานะออกมา ไม่ยัดไว้ข้างป้าย: ข้อความ error ยาวแล้วจะซ้อนกัน -->
    <div class="link" style="margin:-4px 0 12px;align-items:flex-start">
      <span class="dot" id="dot" style="margin-top:5px"></span>
      <span id="status" style="line-height:1.7">กำลังตรวจสอบ…</span>
    </div>

    <div class="styles" id="styles"></div>

    <div style="margin-top:22px"><button class="primary" id="play" disabled>ไปเล่น →</button></div>
    <div class="row" style="margin-top:10px">
      <button class="ghost" id="dl" disabled>ดาวน์โหลด</button>
      <button class="ghost" id="del">ลบข้อมูลของฉัน</button>
    </div>

    <div class="foot">
      <span>PLAYER <b>${s.playerId}</b></span>
      <span>${s.nickname ? s.nickname : "—"}</span>
    </div>
    <div class="err" id="err"></div>
  `;

  const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;
  const statusEl = $("status");
  const dotEl = $("dot");
  const stylesEl = $("styles");
  const playBtn = $<HTMLButtonElement>("play");
  const dlBtn = $<HTMLButtonElement>("dl");

  const drawStyles = (urls: Session["urls"]) => {
    stylesEl.innerHTML = ([1, 2, 3, 4] as StyleId[])
      .map((n) => {
        const url = urls?.[`s${n}` as "s1" | "s2" | "s3" | "s4"];
        const [w, h] = AVATAR[n];
        // ครอปเหลือเฟรม idle เฟรมเดียว — กรอบกว้างเต็มการ์ด สูงตามสัดส่วนเฟรม
        const inner = url
          ? `<span class="frame" style="width:100%;aspect-ratio:${w}/${h};--frames:${FRAMES_PER_SHEET}">
               <img src="${url}" alt="แบบ ${n}" />
             </span>`
          : `<div class="wait">กำลังสร้าง…</div>`;
        return `<div class="sty ${url ? "on" : ""}">
            ${inner}
            <em>${LABEL[n].name}</em>
            <i>${LABEL[n].base} · ${w}×${h}</i>
          </div>`;
      })
      .join("");
  };
  drawStyles((s.aiStatus ?? "pending") === "pending" && s.status === "ready" ? undefined : s.urls);

  playBtn.addEventListener("click", () => { location.href = "/play"; });

  dlBtn.addEventListener("click", () => {
    for (const n of [1, 2, 3, 4] as StyleId[]) {
      const url = s.urls?.[`s${n}` as "s1" | "s2" | "s3" | "s4"];
      if (!url) continue;
      const a = document.createElement("a");
      a.href = url;
      a.download = `player-you-${s.playerId}-s${n}.png`;
      a.click();
    }
  });

  $("del").addEventListener("click", () => {
    if (!confirm("ลบรูปถ่ายและตัวละครของคุณออกจากระบบ?")) return;
    void fetch(`${ENV.apiBase}/api/session/${s.sessionId}`, { method: "DELETE" })
      .catch(() => undefined)
      .finally(() => {
        clearSession();
        root.innerHTML = `
          <div class="title">PLAYER YOU</div>
          <div class="kick">DATA DELETED</div>
          <div class="lbl">ลบแล้ว</div>
          <div class="note">ขอบคุณที่มาร่วมงานครับ</div>`;
      });
  });

  // poll จนกว่าจะ ready — เป้าหมายคือไม่เกิน 5 วินาที
  let tries = 0;
  const tick = async (): Promise<void> => {
    tries++;
    try {
      const res = await fetch(`${ENV.apiBase}/api/session/${s.sessionId}`, { cache: "no-store" });
      const body = (await res.json()) as {
        status: Session["status"];
        urls?: Session["urls"];
        degraded?: boolean;
        failReason?: string;
        aiStatus?: string;
      };
      s.status = body.status;
      if (body.urls) s.urls = body.urls;
      saveSession(s);

      if (body.status === "ready") {
        dotEl.classList.add("live");
        const secs = ((Date.now() - s.createdAt) / 1000).toFixed(1);
        // sprite ถูกเขียนสองรอบ: ฉบับสำรองจาก renderer ตอน ready แล้ว AI เขียนทับทีหลัง
        const ai = body.aiStatus ?? "pending";
        s.aiStatus = ai;
        saveSession(s);

        if (ai === "pending") {
          // เจ้าของงานสั่ง: **ห้ามโชว์ภาพร่าง** ระหว่างรอ AI — โชว์ก็ถูกเข้าใจว่า
          // เป็นผลสุดท้ายทุกที · ระหว่างรอจึงเห็นแค่กล่อง "กำลังวาด…" ล้วนๆ
          // (ภาพร่างยังมีอยู่ใน R2 เผื่อ AI ล้ม — ค่อยโชว์พร้อมป้ายเตือนตอนนั้น)
          statusEl.innerHTML =
            `<span style="color:var(--lime)">กำลังวาดตัวละครด้วย AI… ราวครึ่งนาที</span>`;
          drawStyles(undefined);
          setTimeout(() => void tick(), 1500);
          return;
        }

        drawStyles(s.urls);
        playBtn.disabled = false;
        dlBtn.disabled = false;
        dlBtn.classList.add("on");
        if (ai === "failed") {
          statusEl.innerHTML =
            `พร้อมแล้ว · <span style="color:#e6a23c">AI วาดไม่สำเร็จ — ใช้ตัวละครสำรอง</span>`;
          return;
        } else if (ai === "partial") {
          statusEl.innerHTML =
            `พร้อมแล้ว · <span style="color:#e6a23c">AI วาดได้ไม่ครบทุกแบบ</span>`;
          return;
        } else {
          statusEl.innerHTML = body.degraded
            ? `พร้อมแล้ว · <span style="color:#7a7a8a">อ่านรูปไม่ชัด ใช้ลักษณะมาตรฐาน</span>`
            : `พร้อมแล้ว · ${secs} วินาที`;
          return;
        }
      }
      if (body.status === "failed") {
        statusEl.textContent = `สร้างไม่สำเร็จ: ${body.failReason ?? "ไม่ทราบสาเหตุ"}`;
        return;
      }
      statusEl.textContent = body.status === "generating" ? "กำลังวาดตัวละคร…" : "เข้าคิวแล้ว…";
    } catch (e) {
      statusEl.textContent = `เชื่อมต่อไม่ได้ (${String(e)}) กำลังลองใหม่…`;
    }
    // ถี่ตอนแรกเพราะปกติ ready ใน 5 วิ แล้วค่อยห่างออก
    // ยังต้องยิงต่อหลัง ready เพื่อรอ AI ซึ่งใช้เวลาอีกราว 15 วินาที
    setTimeout(() => void tick(), tries < 12 ? 700 : 2500);
  };
  void tick();
}
